import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const lines = [
  { text: "Fresh.", color: "text-watermelon" },
  { text: "Fun.", color: "text-ocean" },
  { text: "Fueled by Nature.", color: "text-leaf" },
];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const FloatBottle = ({ src, alt, className, delay = 0, amplitude = 18, rotate = 0, testid }) => (
  <motion.img
    src={src}
    alt={alt}
    data-testid={testid}
    className={className}
    initial={{ opacity: 0, y: 60, scale: 0.9 }}
    animate={{
      opacity: 1,
      y: [0, -amplitude, 0],
      scale: 1,
      rotate: [rotate, rotate + 2, rotate],
    }}
    transition={{
      opacity: { duration: 0.8, delay: 0.4 + delay },
      scale: { duration: 0.8, delay: 0.4 + delay },
      y: { duration: 6, repeat: Infinity, ease: "easeInOut", delay },
      rotate: { duration: 7, repeat: Infinity, ease: "easeInOut", delay },
    }}
  />
);

export default function Hero() {
  return (
    <section id="hero" className="relative min-h-[100svh] overflow-hidden pt-28 pb-16 sm:pt-32">
      {/* watercolor blobs */}
      <div className="wc-blob" style={{ background: "#FF4D6D", width: 420, height: 420, top: -60, left: -80 }} />
      <div className="wc-blob" style={{ background: "#0077B6", width: 460, height: 460, bottom: -120, right: -100 }} />
      <div className="wc-blob" style={{ background: "#8AC926", width: 300, height: 300, top: "40%", left: "45%", opacity: 0.35 }} />

      <div className="relative z-10 mx-auto max-w-6xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="flex justify-center"
        >
          <img src="/assets/logo-cut.png" alt="ZoJu Juices logo" className="h-24 sm:h-32 w-auto" data-testid="hero-logo" />
        </motion.div>

        <div className="mt-8 grid items-center gap-8 lg:grid-cols-[1.1fr_0.9fr]">
          {/* Left: kinetic type */}
          <div className="text-center lg:text-left">
            <h1 className="font-display font-extrabold uppercase leading-[0.9] tracking-tighter text-5xl sm:text-7xl lg:text-[5.5rem]">
              {lines.map((l, i) => (
                <MaskedLine key={l.text} delay={0.15 + i * 0.14}>
                  <span className={l.color}>{l.text}</span>
                </MaskedLine>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="mx-auto lg:mx-0 mt-6 max-w-md text-base sm:text-lg text-ink/70"
            >
              All-natural fresh juice, hand-crafted in Orlando by 11-year-old founder Zoey — made to fuel young athletes the natural way.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.6 }}
              className="mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-4"
            >
              <button
                onClick={() => scrollTo("order")}
                data-testid="hero-order-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-watermelon px-8 py-4 text-base font-semibold text-white shadow-[0_12px_30px_rgba(255,77,109,0.35)] transition-transform duration-200 hover:scale-105 active:scale-95"
              >
                Order Now
                <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
              </button>
              <button
                onClick={() => scrollTo("products")}
                data-testid="hero-explore-btn"
                className="rounded-full border-2 border-ink/15 px-7 py-4 text-base font-semibold text-ink transition-colors hover:border-ink/40"
              >
                See the flavors
              </button>
            </motion.div>
          </div>

          {/* Right: floating bottles */}
          <div className="relative h-[340px] sm:h-[460px] lg:h-[540px]">
            <FloatBottle
              testid="hero-bottle-watermelon"
              src="/assets/watermelon-cut.png"
              alt="Watermelon Juice bottle"
              className="absolute left-[6%] bottom-0 w-[46%] max-w-[240px] drop-shadow-[0_25px_35px_rgba(255,77,109,0.28)]"
              delay={0}
              amplitude={20}
              rotate={-4}
            />
            <FloatBottle
              testid="hero-bottle-seamoss"
              src="/assets/seamoss-cut.png"
              alt="Blue Sea Moss Lemonade bottle"
              className="absolute right-[6%] top-0 w-[46%] max-w-[240px] drop-shadow-[0_25px_35px_rgba(0,119,182,0.28)]"
              delay={0.8}
              amplitude={16}
              rotate={5}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
